import React from 'react';
import type { ComponentProps } from 'react';
import cx from 'classnames';
import { useTranslations } from '@unisporkal/localization';
import {
  ListItem as AllianceListItem,
  RadioButton,
  Spinner,
} from '@unisporkal/alliance-ui';
import { InfoTooltip } from '../../molecules/InfoTooltip';
import { getAvailablePaRowText } from './agreementText';
import type { PremiumAccessAgreementSummary } from './types';
import styles from './BatchAgreements.module.scss';

// ListItemProps doesn't declare onClick, though the component forwards
// unrecognized props onto its underlying <li> — widen locally rather than
// resorting to an untyped prop spread.
const ListItem = AllianceListItem as React.FC<
  ComponentProps<typeof AllianceListItem> & {
    onClick?: (event: React.MouseEvent<HTMLElement>) => void;
  }
>;

interface PaAgreementOptionProps {
  paAgreement: PremiumAccessAgreementSummary;
  isSelected: boolean;
  isSwitchingToThis: boolean;
  disabled: boolean;
  onSelect: () => void;
}

const PaAgreementOption: React.FC<PaAgreementOptionProps> = ({
  paAgreement,
  isSelected,
  isSwitchingToThis,
  disabled,
  onSelect,
}) => {
  const t = useTranslations();

  const handleRowClick = (event: React.MouseEvent<HTMLElement>) => {
    if (disabled) return;
    const target = event.target as HTMLElement;
    // RadioButton renders its own <label> wrapping the input, the circle,
    // and the label text. A click anywhere inside that label already
    // toggles the radio via native label-forwarding + onChange, so this
    // handler only needs to fire onSelect for clicks landing outside it
    // (the row's padding, the tooltip, the spinner).
    if (target.closest('label')) return;
    onSelect();
  };

  const rowStateClass = () => {
    if (disabled) return isSelected ? styles.selected : styles.disabledRow;
    return isSelected ? styles.selected : styles.clickable;
  };

  return (
    // TODO(CU-86bbzjx4z): extract into a shared selectable-row-with-radio
    // component — ImageSize/FilmSize/UltraPackListItem hand-roll the same
    // pattern with a plain label/input.
    <ListItem
      className={cx(styles.row, rowStateClass())}
      data-testid="agreement-details"
      onClick={handleRowClick}
    >
      <RadioButton
        id={`batch-agreement-${paAgreement.id}`}
        name="batch-pa-agreement"
        value={paAgreement.id}
        checked={isSelected}
        disabled={disabled}
        onChange={onSelect}
        className={styles.radio}
      >
        {getAvailablePaRowText(paAgreement, t)}
      </RadioButton>
      {paAgreement.isExhausted && (
        <InfoTooltip
          content={t('overage_tooltip')}
          tooltipClassName={styles.overageTooltip}
        />
      )}
      {isSwitchingToThis && (
        <Spinner
          decorative
          className={styles.spinner}
          data-testid="agreement-switching-spinner"
        />
      )}
    </ListItem>
  );
};

export default PaAgreementOption;
